import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm, useFieldArray, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useHeaderExtra } from '@/contexts/header.context'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CopyButton } from '@/components/ui/CopyButton'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormChangeTracking,
} from '@/components/ui/form'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { toast } from 'sonner'
import { ArrowLeft, Plus, X } from 'lucide-react'
import { triggersApiClient } from '@/lib/api'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  TRIGGER_VARIABLE_TYPE_VALUES,
  triggerNameToEventName,
  isBuiltInTriggerEvent,
  type Trigger,
  type TriggerVariableType,
} from '@super/shared-types'
import { buildPayloadPreview } from './payload-preview'

// Source is implicit (WEBHOOK) until INTEGRATION/CRON ship; we set it on create
// and hide the picker until there's a real choice to make.
const DEFAULT_SOURCE = 'WEBHOOK' as const

// Display labels for the type dropdown. Values come from the centralized
// TRIGGER_VARIABLE_TYPE_VALUES; only the labels live here (presentation).
const TYPE_LABELS: Record<TriggerVariableType, string> = {
  phone: 'Phone (E164)',
  email: 'Email',
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Boolean',
  url: 'URL',
}

const isContactChannelType = (type: TriggerVariableType) =>
  type === 'phone' || type === 'email'

// Allow camelCase / snake_case identifiers. Anchored so partial matches don't
// pass; integrators will reference these in templates as `{{name}}`.
const VARIABLE_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/

const variableSchema = z.object({
  name: z
    .string()
    .min(1, 'Variable name is required')
    .max(100, 'Variable name is too long')
    .regex(
      VARIABLE_NAME_PATTERN,
      'Use letters, digits, or underscores; start with a letter',
    ),
  type: z.enum(TRIGGER_VARIABLE_TYPE_VALUES),
  required: z.boolean(),
})

const formSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .max(80, 'Name is too long')
      .refine(
        value => !!triggerNameToEventName(value),
        'Name must contain at least one letter or number',
      ),
    description: z.string().max(500, 'Description is too long').nullable(),
    active: z.boolean(),
    variables: z.array(variableSchema).min(1, 'Add at least one variable'),
  })
  .superRefine((values, ctx) => {
    // At least one phone or email — the contact channel.
    const hasContactChannel = values.variables.some(v =>
      isContactChannelType(v.type),
    )
    if (!hasContactChannel) {
      ctx.addIssue({
        code: 'custom',
        path: ['variables'],
        message:
          'Add an item with type "Phone" or "Email" so the agent can reach the contact',
      })
    }

    // Names must be unique. Surface the error on the duplicate row so the
    // inline message lands where the user can fix it.
    const seen = new Map<string, number>()
    values.variables.forEach((variable, index) => {
      const trimmed = variable.name.trim()
      if (!trimmed) return
      if (seen.has(trimmed)) {
        ctx.addIssue({
          code: 'custom',
          path: ['variables', index, 'name'],
          message: 'Variable names must be unique',
        })
      } else {
        seen.set(trimmed, index)
      }
    })
  })

type FormValues = z.infer<typeof formSchema>

const DEFAULT_VALUES: FormValues = {
  name: '',
  description: null,
  active: true,
  variables: [{ name: 'phone', type: 'phone', required: true }],
}

export function TriggerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'

  useDocumentHead({
    title: isNew ? 'New Trigger' : 'Trigger',
    description: 'Configure a trigger that starts an outbound agent',
  })

  const navigate = useNavigate()
  const { currentCompany, isSuperAdmin } = useAppAuth()

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [existing, setExisting] = useState<Trigger | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const variables = useFieldArray({
    control: form.control,
    name: 'variables',
  })

  useEffect(() => {
    const load = async () => {
      if (!currentCompany?.id) {
        if (isNew) setLoading(false)
        return
      }
      if (!isNew && id) {
        const list = isSuperAdmin
          ? await triggersApiClient.getDefinitionsForCompanyByAdmin(
              currentCompany.id,
            )
          : await triggersApiClient.getDefinitions()
        const trigger = list.find(item => item.id === id) ?? null
        if (trigger) {
          setExisting(trigger)
          // Trigger comes back already normalized — every variable has a `type`.
          form.reset({
            name: trigger.name,
            description: trigger.description ?? null,
            active: trigger.active,
            variables: trigger.variables.map(variable => ({
              name: variable.name,
              type: variable.type ?? 'text',
              required: variable.required ?? false,
            })),
          })
        } else {
          toast.error('Trigger not found')
          navigate(ROUTE_REGISTRY.TRIGGERS.path)
        }
      }
      setLoading(false)
    }
    load()
  }, [id, isNew, currentCompany?.id, navigate, form])

  const isBuiltIn = !!existing && isBuiltInTriggerEvent(existing.eventName)

  const onSubmit = async (values: FormValues) => {
    if (!currentCompany?.id) return

    const trimmedName = values.name.trim()
    const eventName = existing?.eventName ?? triggerNameToEventName(trimmedName)

    // Phone and email are an at-least-one-present group at ingestion, not each
    // individually required, so the author's choice is saved as made. The
    // schema above refuses a trigger carrying neither.
    const variablesForSave = values.variables.map(variable => ({
      name: variable.name.trim(),
      type: variable.type,
      required: variable.required,
    }))

    const body = {
      name: trimmedName,
      description: values.description?.trim() || null,
      variables: variablesForSave,
      sourceConfig: null,
      active: values.active,
    }

    setSaving(true)
    try {
      if (existing) {
        const updated = isSuperAdmin
          ? await triggersApiClient.updateTriggerForCompany(
              existing.id,
              currentCompany.id,
              body,
            )
          : await triggersApiClient.updateTrigger(existing.id, body)
        if (!updated) {
          toast.error('Failed to update trigger')
          return
        }
        toast.success(`Trigger "${updated.name}" updated`)
        setExisting(updated)
        // Reset the form to the just-saved values so isDirty becomes false and
        // the floating save island hides until the next change.
        form.reset({
          name: updated.name,
          description: updated.description ?? null,
          active: updated.active,
          variables: updated.variables.map(variable => ({
            name: variable.name,
            type: variable.type ?? 'text',
            required: variable.required ?? false,
          })),
        })
      } else {
        const created = isSuperAdmin
          ? await triggersApiClient.createTriggerForCompany(currentCompany.id, {
              ...body,
              source: DEFAULT_SOURCE,
              eventName,
            })
          : await triggersApiClient.createTrigger({
              ...body,
              source: DEFAULT_SOURCE,
              eventName,
            })
        if (!created) {
          toast.error('Failed to create trigger')
          return
        }
        toast.success(`Trigger "${created.name}" created`)
        // Return to the list so the user sees their new trigger in context.
        navigate(ROUTE_REGISTRY.TRIGGERS.path)
      }
    } finally {
      setSaving(false)
    }
  }

  useHeaderExtra(
    <div className="flex items-center">
      <Link
        to={ROUTE_REGISTRY.TRIGGERS.path}
        className="inline-flex items-center gap-1.5 rounded-md px-2 h-8 text-sm text-muted-foreground hover:bg-accent transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </Link>
    </div>,
    [],
  )

  if (loading) {
    return (
      <div className="h-full overflow-y-auto pt-16">
        <div className="space-y-4 p-6">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-40 w-full max-w-2xl" />
          <Skeleton className="h-40 w-full max-w-2xl" />
        </div>
      </div>
    )
  }

  // Array-level errors on `variables` (empty array, missing contact channel,
  // etc.) land in two possible spots depending on the RHF / zod version —
  // `variables.message` for zod refinements at the array path, and
  // `variables.root.message` for the array's own .min() check. Read both.
  const variablesError = form.formState.errors.variables
  const variablesArrayError =
    (variablesError?.message as string | undefined) ??
    (variablesError?.root?.message as string | undefined)
  const hasVariablesError = !!variablesArrayError

  const watchedName = form.watch('name')
  const resolvedEventName = existing
    ? existing.eventName
    : triggerNameToEventName(watchedName ?? '')
  const watchedVariables = form.watch('variables')
  const preview = buildPayloadPreview({
    eventName: resolvedEventName,
    variables: watchedVariables,
  })

  const onInvalid = () => {
    // Surface a toast so users who skip past the inline error messages still
    // know why the save action didn't fire. Matches the agent forms pattern.
    toast.error('Please fix the highlighted fields and try again')
  }

  return (
    <FloatingActionIslandProvider>
      <div className="h-full overflow-y-auto pt-16">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            <div className="p-6 pt-0 pb-24">
              <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                <div className="w-full max-w-2xl space-y-6">
                  {!isNew && (
                    <div className="flex justify-end">
                      <Link
                        to={`${ROUTE_REGISTRY.TRIGGERS_EVENTS.path}?triggerId=${id}`}
                        className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        View events for this trigger →
                      </Link>
                    </div>
                  )}
                  <div className="space-y-4 border rounded-lg p-5">
                    {/* Row 1: Name (left, flex-1) + Active toggle (right). */}
                    <div className="flex items-start gap-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="flex-1 min-w-0">
                            <FormLabel>Display name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Pest Control Notice"
                                {...field}
                                disabled={isBuiltIn}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {isBuiltIn ? (
                        <div className="pt-7 shrink-0">
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                            Active
                          </Badge>
                        </div>
                      ) : (
                        <FormField
                          control={form.control}
                          name="active"
                          render={({ field }) => (
                            <FormItem className="flex items-center gap-2 pt-7 shrink-0 space-y-0">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <FormLabel className="text-sm cursor-pointer m-0 p-0">
                                {field.value ? 'Active' : 'Inactive'}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      )}
                    </div>

                    <FormItem>
                      <FormLabel>Event name</FormLabel>
                      <FormControl>
                        <Input
                          value={
                            existing?.eventName ??
                            triggerNameToEventName(form.watch('name') ?? '')
                          }
                          readOnly
                          disabled
                          className="font-mono"
                        />
                      </FormControl>
                      <FormDescription>
                        {existing
                          ? 'Stays the same even if you rename this trigger.'
                          : 'Derived from the display name when you save. Stays the same even if you rename later.'}
                      </FormDescription>
                    </FormItem>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea
                              className="min-h-[60px]"
                              {...field}
                              value={field.value ?? ''}
                              disabled={isBuiltIn}
                            />
                          </FormControl>
                          <FormDescription>
                            Include any helpful description about when this
                            trigger will fire.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div
                    className={`space-y-3 border rounded-lg p-5 transition-colors ${
                      hasVariablesError ? 'border-destructive' : ''
                    }`}
                  >
                    <div>
                      <h3 className="ml-3 text-sm font-medium">Variables</h3>
                      <p className="ml-3 text-xs text-muted-foreground">
                        Variable fields are described here and then used within
                        the agent settings.
                      </p>
                      {hasVariablesError && (
                        <p className="ml-3 mt-2 text-xs text-destructive font-medium">
                          {variablesArrayError}
                        </p>
                      )}
                    </div>

                    {/* Column header row (label-only, no input). Mirrors the
                        widths of the rows below so headers line up over their
                        fields without needing a table. */}
                    {variables.fields.length > 0 && (
                      <div className="ml-3 flex items-center gap-3 text-xs font-medium text-muted-foreground">
                        <div className="flex-1">Name</div>
                        <div className="w-40">Type</div>
                        <div className="w-20 text-center">Required</div>
                        <div className="w-9" />
                      </div>
                    )}

                    <div className="space-y-2">
                      {variables.fields.map((row, index) => {
                        return (
                          <VariableRow
                            key={row.id}
                            index={index}
                            isBuiltIn={isBuiltIn}
                            control={form.control}
                            onRemove={() => variables.remove(index)}
                          />
                        )
                      })}
                    </div>

                    {!isBuiltIn && (
                      <div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            variables.append({
                              name: '',
                              type: 'text',
                              required: false,
                            })
                          }
                          /*
                           * Sits flush with the inputs above — Button's outer
                           * left edge at x=0, same as the Input's outer left
                           * edge. The icon inside is offset by Button's own
                           * px-2.5; that's expected and consistent with how
                           * every other ghost-Button + icon renders.
                           */
                          className="h-8 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1.5" />
                          Add variable
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-full lg:w-96 lg:sticky lg:top-6 lg:self-start">
                  <div className="space-y-2 border rounded-lg p-5">
                    <div>
                      <h3 className="ml-3 text-sm font-medium">
                        Sample payload
                      </h3>
                      <p className="ml-3 text-xs text-muted-foreground">
                        What to POST to{' '}
                        <span className="font-mono">/api/webhooks/event</span>.
                        Send one object for a single call, or an array to dial a
                        batch.
                      </p>
                    </div>
                    <Tabs defaultValue="single" className="w-full">
                      <TabsList
                        className="grid w-full grid-cols-2"
                        mobileDropdown={false}
                      >
                        <TabsTrigger value="single">Single</TabsTrigger>
                        <TabsTrigger value="batch">Batch</TabsTrigger>
                      </TabsList>
                      <TabsContent value="single" className="mt-6">
                        <PreviewBlock json={preview.single} />
                      </TabsContent>
                      <TabsContent value="batch" className="mt-6">
                        <PreviewBlock json={preview.batch} />
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>

            <TriggerSaveIsland
              existing={existing}
              isNew={isNew}
              saving={saving}
            />

            <FloatingActionIsland />
          </form>
        </Form>
      </div>
    </FloatingActionIslandProvider>
  )
}

/**
 * Floating save island — mirrors AgentDetailSaveIsland. Shows whenever the
 * form is dirty (post-load changes) or when we're on the create route (the
 * user is here to create, keep the action visible).
 */
function TriggerSaveIsland({
  existing,
  isNew,
  saving,
}: {
  existing: Trigger | null
  isNew: boolean
  saving: boolean
}) {
  const { hasUnsavedChanges, changedFieldsCount } = useFormChangeTracking()
  const shouldShow = hasUnsavedChanges || isNew

  return (
    <FloatingActionIslandContent
      show={shouldShow}
      componentId="trigger-detail-save"
      priority={20}
    >
      <div className="inline-flex w-full max-w-2xl">
        <div className="flex flex-grow items-center mr-4 space-x-2 text-sm text-gray-600 dark:text-muted-foreground whitespace-nowrap pointer-events-none">
          <span className="font-medium">
            {isNew && !hasUnsavedChanges
              ? 'New trigger ready to create'
              : `${changedFieldsCount} unsaved ${
                  changedFieldsCount === 1 ? 'change' : 'changes'
                }`}
          </span>
        </div>
        <div className="flex justify-end items-center space-x-2 pointer-events-auto">
          <Button type="submit" disabled={saving} size="sm" variant="form">
            {saving
              ? 'Saving…'
              : existing
                ? 'Update trigger'
                : 'Create trigger'}
          </Button>
        </div>
      </div>
    </FloatingActionIslandContent>
  )
}

/**
 * One row in the variables list. Rendered without FormItem on purpose: the
 * stock FormItem is `grid gap-2` and reserves a second grid row for the
 * FormMessage even when no error is present, which pushes the Input's
 * vertical center below the bare Switch / X-delete in the same flex row.
 *
 * Here every cell's container is exactly the height of its visible control
 * (or a fixed h-9 to match Input height), and per-field errors render below
 * the row so they never alter row-cell heights.
 */
function VariableRow({
  index,
  isBuiltIn,
  control,
  onRemove,
}: {
  index: number
  isBuiltIn: boolean
  control: Control<FormValues>
  onRemove: () => void
}) {
  return (
    <FormField
      control={control}
      name={`variables.${index}`}
      render={() => (
        <div>
          {/* Inputs row — flush with the card's content edge, matching the
              FormItem convention where the Input sits at x=0 while the label
              and error are indented (ml-3). items-center keeps every cell on
              the same vertical center. */}
          <div className="flex items-center gap-3">
            <FormField
              control={control}
              name={`variables.${index}.name`}
              render={({ field }) => (
                <Input
                  placeholder="variableName"
                  className="flex-1 font-mono"
                  {...field}
                  disabled={isBuiltIn}
                />
              )}
            />
            <FormField
              control={control}
              name={`variables.${index}.type`}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={value =>
                    field.onChange(value as TriggerVariableType)
                  }
                  disabled={isBuiltIn}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_VARIABLE_TYPE_VALUES.map(type => (
                      <SelectItem key={type} value={type}>
                        {TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FormField
              control={control}
              name={`variables.${index}.required`}
              render={({ field }) => (
                <div className="w-20 flex justify-center">
                  <Switch
                    checked={field.value}
                    disabled={isBuiltIn}
                    onCheckedChange={field.onChange}
                    aria-label="Required"
                  />
                </div>
              )}
            />
            <div className="w-9 flex justify-center">
              {!isBuiltIn && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onRemove}
                  aria-label={`Remove variable ${index + 1}`}
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              )}
            </div>
          </div>
          {/* Per-field error messages render under the row so they never
              affect the input row's height (and therefore can never throw
              the Switch / X-delete off-center). */}
          <VariableRowError control={control} index={index} field="name" />
          <VariableRowError control={control} index={index} field="type" />
        </div>
      )}
    />
  )
}

function VariableRowError({
  control,
  index,
  field,
}: {
  control: Control<FormValues>
  index: number
  field: 'name' | 'type'
}) {
  return (
    <FormField
      control={control}
      name={`variables.${index}.${field}`}
      render={({ fieldState }) =>
        // Match the default FormMessage class so per-variable errors line up
        // with every other form error on the page (Name field, etc.). mt-2
        // mimics the FormItem's grid `gap-2` so the vertical breathing room
        // matches what FormMessage gets when it lives inside a FormItem.
        fieldState.error ? (
          <p className="text-destructive text-xs ml-3 mt-2">
            {fieldState.error.message}
          </p>
        ) : (
          <></>
        )
      }
    />
  )
}

function PreviewBlock({ json }: { json: string }) {
  return (
    <div className="relative">
      <CopyButton
        value={json}
        size="icon"
        variant="ghost"
        className="absolute right-2 top-2 h-7 w-7"
      />
      <pre className="overflow-x-auto rounded-md bg-muted/50 p-3 pr-10 text-xs font-mono leading-relaxed">
        {json}
      </pre>
    </div>
  )
}
